import { useEffect, useRef, useState } from 'react';
import { Mixpanel } from '../../utils/mixpanel';
import { AICA_DOWNLOAD_DATA } from '../../utils/constants/mixpanelEvents/investorEvents';
import { setRolesForUser } from '../../store/user/userV2/reducer';
import { getTimeStamp } from '../../utils/dateUtils';
import { exportRawFromUnderwritingAndPolus } from '../../store/company/apiHelpers';
import { useAppSelector } from '../../app/hooks';
import { getFeatureStatus, isRestrictedUser } from '../../utils/utils';
import { RESTRICTED_ROLES } from '../../store/investor/constants';
import { VIEW_CONFIGS } from '../../utils/constants/commonConstants';
import { FEATURES } from '../../store/user/constants';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fetchInvestorConfig, fetchLenderConfig, fetchSingleCompanyDetails, updateDealApplicationData } from '../../store/investor/action';
import { useDispatch } from 'react-redux';
import { changeOrgIdHeaderForCharts } from '../../utils/axios-interceptor';
import axios from 'axios';
import { USER_TYPE } from '../../utils/constants/user';
import { fetchAicaGradeData } from '../../store/dashboard/actions';
import { updateQueryParams } from '../../utils/searchParamUtils';

const useLegacyBusinessDataFetch = ({ fetchAicaGrade = false, assesseeOrgId = '' } = {}) => {
    const [exportingData, setExportingData] = useState(false);
    const user = useAppSelector((state) => state.user);
    const [selectedCompany, setSelectedCompany] = useState<any>({});
    const isUserRestricted = isRestrictedUser(user?.userData?.roles?.[0], RESTRICTED_ROLES);
    const availableFeatures = user.domainMetadata?.availableFeatures || [];
    const refreshReportStatus = getFeatureStatus(availableFeatures || [], FEATURES.REFRESH_REPORT);
    const downloadPDFStatus = getFeatureStatus(availableFeatures || [], FEATURES.DOWNLOAD_PDF);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const params = useParams();
    const refetchOverviewRef = useRef<any>(null);

    const exportData = (stopAdditional = true) => {
        setExportingData(true);
        Mixpanel.track(AICA_DOWNLOAD_DATA, {
            Role: setRolesForUser(user.userData.roles?.[0]),
            InvestorOrgId: user.userData.investorOrgId,
            CompanyOrgId: selectedCompany?.assesseeOrgId,
            AICASection: selectedCompany?.companyAccessType,
            Timestamp: getTimeStamp(),
        });
        exportRawFromUnderwritingAndPolus(
            {
                assesseeOrgId: selectedCompany?.assesseeOrgId,
                orgName: selectedCompany?.orgName,
            },
            {
                onSuccess: () => {
                    setExportingData(false);
                },
                onError: () => {
                    setExportingData(false);
                },
            },
            stopAdditional,
        );
    };

    useEffect(() => {
        if (!searchParams.get('page')) {
            updateQueryParams(searchParams, setSearchParams, { page: '1' }, location);
        }
    }, [searchParams]);

    const navigateBasedOnAccountType = (accountType) => {
        switch (accountType) {
            case USER_TYPE.INVESTOR:
                navigate(
                    `/investor/app/${
                        selectedCompany?.companyAccessType?.toLowerCase() === 'track'
                            ? 'monitor'
                            : selectedCompany?.companyAccessType?.toLowerCase() || 'watch'
                    }?fetchType=${searchParams.get('fetchType') ?? 'all'}&page=${searchParams.get('page') ?? 1}&type=${
                        selectedCompany?.companyAccessType?.toLowerCase() || 'scan'
                    }`,
                    { state: location.state },
                );
                break;
            case USER_TYPE.LENDER_TEAM:
            case USER_TYPE.LENDER:
                navigate(`/lender/app/deals?page=${searchParams.get('page')}&type=deals`, {
                    state: location.state,
                });
                break;
            default:
                navigate(
                    `/investor/app/${
                        selectedCompany?.companyAccessType?.toLowerCase() === 'track'
                            ? 'monitor'
                            : selectedCompany?.companyAccessType?.toLowerCase() || 'watch'
                    }?fetchType=${searchParams.get('fetchType') ?? 'all'}&page=${searchParams.get('page') ?? 1}&type=${
                        selectedCompany?.companyAccessType?.toLowerCase() || 'scan'
                    }`,
                    { state: location.state },
                );
                break;
        }
    };

    const goBackToPortfolio = () => {
        searchParams.delete('selectedTab');
        setSearchParams(searchParams);

        if (searchParams.get('source') === 'deals' && user?.userData?.accountType === USER_TYPE.INVESTOR) {
            searchParams.delete('source');
            setSearchParams(searchParams);
            navigate('/investor/app/deals');
            return;
        }

        switch (location.state?.from) {
            case 'WATCH':
                navigate('/investor/app/watch');
                break;
            case 'SCAN':
                navigate('/investor/app/scan');
                break;
            case 'TRACK':
                navigate('/investor/app/monitor');
                break;
            case 'MONITOR':
                navigate('/investor/app/monitor');
                break;
            case 'DEALS':
                navigate('/lender/app/deals');
                break;
            default:
                navigateBasedOnAccountType(user?.userData?.accountType);
                break;
        }
    };

    useEffect(() => {
        if ((params.id || assesseeOrgId) && user.domainMetadata?.investorOrgId) {
            dispatch(
                fetchSingleCompanyDetails(
                    {
                        assesseeOrgId: params.id ?? assesseeOrgId,
                        investorOrgId: user.domainMetadata?.investorOrgId,
                    },
                    {
                        onSuccess: (companyData) => setSelectedCompany(companyData),
                    },
                ),
            );
            if (fetchAicaGrade) {
                dispatch(
                    fetchAicaGradeData({
                        assesseeOrgId: params.id ?? assesseeOrgId,
                        investorOrgId: user.domainMetadata?.investorOrgId,
                    }),
                );
            }
        }
    }, [user.domainMetadata?.investorOrgId, params.id, assesseeOrgId]);

    useEffect(() => {
        if (user.userData.primaryEmail && user.userData.investorOrgId && params?.id) {
            (params?.id || assesseeOrgId) && changeOrgIdHeaderForCharts(axios, params.id);
            dispatch(
                fetchInvestorConfig({
                    accountType: user.userData.accountType,
                    primaryEmail: user.userData.primaryEmail,
                    investorOrgId: user.userData.investorOrgId,
                    assesseeOrgId: params.id,
                    reportId: selectedCompany?.reportId || '',
                    userRole: user?.userData?.roles?.[0],
                    configName: 'AICA_DASHBOARD',
                }),
            );
            dispatch(
                fetchLenderConfig({
                    accountType: user.userData.accountType,
                    primaryEmail: user.userData.primaryEmail,
                    investorOrgId: user.userData.investorOrgId,
                    assesseeOrgId: params.id,
                    userRole: user?.userData?.roles?.[0],
                    configName: VIEW_CONFIGS.AICA_DEBT_PRODUCT,
                }),
            );
        }

        return () => {
            const investorOrgId = user.userData.investorOrgId;
            changeOrgIdHeaderForCharts(axios, investorOrgId);
        };
    }, [user.userData.primaryEmail, user.userData.investorOrgId, params?.id, assesseeOrgId]);

    const onUnlockReportSuccess = (callbackFunction = () => {}) => {
        refetchOverviewRef.current = true;
        dispatch(
            fetchSingleCompanyDetails(
                {
                    assesseeOrgId: params.id,
                    investorOrgId: user.domainMetadata?.investorOrgId,
                },
                {
                    onSuccess: (companyData) => {
                        callbackFunction?.();
                        setSelectedCompany(companyData);
                    },
                },
            ),
        );
        dispatch(
            fetchAicaGradeData({
                assesseeOrgId: params.id as string,
                investorOrgId: user.domainMetadata?.investorOrgId as string,
            }),
        );
    };

    return {
        exportingData,
        exportData,
        isUserRestricted,
        selectedCompany,
        refreshReportStatus,
        downloadPDFStatus,
        goBackToPortfolio,
        setSelectedCompany,
        user,

        refetchOverviewRef,
        onUnlockReportSuccess,
    };
};

export default useLegacyBusinessDataFetch;
